import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, SpellCheck, AlertTriangle, CheckCircle2, Download } from 'lucide-react';
import { candidatesAPI } from '../../lib/api';
import { buildResumePdf } from './buildResumePdf';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';

const CorrectionRow = ({ c, checked, onToggle, index }) => (
  <label className="flex gap-3 p-3 rounded-lg border border-slate-200 has-[[data-state=checked]]:border-cyan-400 has-[[data-state=checked]]:bg-cyan-50/50 cursor-pointer" data-testid={`resume-correction-${index}`}>
    <Checkbox checked={checked} onCheckedChange={onToggle} className="mt-0.5" data-testid={`resume-correction-check-${index}`} />
    <div className="min-w-0 text-xs space-y-1">
      <p className="text-slate-400 line-through break-words">{c.original}</p>
      <p className="text-slate-900 font-medium break-words">{c.corrigido}</p>
      {c.motivo && <p className="text-cyan-700">{c.motivo}</p>}
    </div>
  </label>
);

const MissingList = ({ items, onEdit }) => (
  <div className="rounded-lg bg-amber-50 border border-amber-200 p-3" data-testid="resume-missing-list">
    <p className="text-sm font-semibold text-amber-800 flex items-center gap-2"><AlertTriangle className="w-4 h-4" />Informações faltando</p>
    <ul className="mt-1 ml-5 list-disc text-xs text-amber-800 space-y-0.5">
      {items.map((m, i) => <li key={i}>{m.label}</li>)}
    </ul>
    <Button variant="link" size="sm" className="px-0 h-auto mt-1 text-amber-900" onClick={onEdit} data-testid="resume-complete-profile">Completar meu perfil</Button>
  </div>
);

export const ResumeGeneratorDialog = ({ open, onClose, email }) => {
  const navigate = useNavigate();
  const [review, setReview] = useState(null);
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setReview(null);
    candidatesAPI.reviewResume()
      .then((r) => { setReview(r.data); setSelected(r.data.corrections.map((_, i) => i)); })
      .catch(() => { toast.error('Não foi possível revisar o currículo'); onClose(); });
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (i) => setSelected((s) => (s.includes(i) ? s.filter((x) => x !== i) : [...s, i]));

  const generate = async () => {
    setBusy(true);
    try {
      const chosen = review.corrections.filter((_, i) => selected.includes(i));
      if (chosen.length) await candidatesAPI.applyResumeCorrections(chosen);
      const profile = (await candidatesAPI.getMe()).data;
      await buildResumePdf(profile, profile.email || email);
      toast.success(chosen.length ? 'Correções salvas e currículo baixado' : 'Currículo baixado');
      onClose();
    } catch {
      toast.error('Erro ao gerar o currículo');
    } finally { setBusy(false); }
  };

  const corrections = review?.corrections || [];
  return (
    <Dialog open={open} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="w-[calc(100%-2rem)] rounded-xl max-w-md max-h-[85vh] overflow-y-auto" data-testid="resume-generator-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><SpellCheck className="w-5 h-5 text-cyan-600" />Gerar currículo em PDF</DialogTitle>
          <DialogDescription>Revisamos seu perfil antes de gerar o currículo.</DialogDescription>
        </DialogHeader>
        {!review ? (
          <div className="py-10 flex flex-col items-center gap-3 text-sm text-slate-500" data-testid="resume-review-loading">
            <Loader2 className="w-6 h-6 animate-spin text-cyan-600" />Revisando português e informações...
          </div>
        ) : (
          <div className="space-y-3">
            {review.missing.length > 0 && <MissingList items={review.missing} onEdit={() => navigate('/m/profile/edit')} />}
            {corrections.length > 0 ? (
              <>
                <p className="text-sm font-semibold text-slate-800">Correções sugeridas ({corrections.length})</p>
                {corrections.map((c, i) => <CorrectionRow key={c.path} c={c} index={i} checked={selected.includes(i)} onToggle={() => toggle(i)} />)}
              </>
            ) : (
              <p className="text-sm text-green-700 flex items-center gap-2" data-testid="resume-no-corrections">
                <CheckCircle2 className="w-4 h-4" />{review.review_ok ? 'Nenhum erro de português encontrado.' : 'Revisão de português indisponível agora.'}
              </p>
            )}
          </div>
        )}
        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={onClose} disabled={busy} data-testid="resume-cancel">Cancelar</Button>
          <Button onClick={generate} disabled={!review || busy} className="bg-cyan-600 hover:bg-cyan-700" data-testid="resume-generate-download">
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />}
            {selected.length ? 'Aplicar e baixar PDF' : 'Baixar PDF'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
